export function PrivacyPolicy() {
  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
      <p className="text-sm text-gray-500 mb-8">Last updated: March 14, 2026</p>

      <div className="space-y-8 text-gray-700 leading-relaxed">
        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Introduction</h2>
          <p>
            SmartCatalog (&quot;we&quot;, &quot;us&quot;, or &quot;our&quot;) is operated by Winding River Software.
            This Privacy Policy explains how we collect, use, and protect your information when you
            use the SmartCatalog application at smartcatalog.app and its subdomains (the &quot;Service&quot;).
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Information We Collect</h2>
          <h3 className="text-lg font-medium text-gray-800 mb-2">Account Information</h3>
          <p className="mb-3">
            When you sign in with Google, we receive your name, email address, and profile picture
            from your Google account. We use this information to create and manage your SmartCatalog
            account.
          </p>
          <h3 className="text-lg font-medium text-gray-800 mb-2">Google Drive Access</h3>
          <p className="mb-3">
            If you choose to connect Google Drive, we request read-only access
            (<code className="bg-gray-100 px-1 rounded">drive.readonly</code>) to allow you to browse
            and select product images from your Drive. We do not modify, delete, or store your
            Drive files. We only access files you explicitly select for import.
          </p>
          <h3 className="text-lg font-medium text-gray-800 mb-2">Product Data</h3>
          <p>
            Product information you create (names, descriptions, images, categories, pricing) is
            stored in our database and synchronized with your connected Square account.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-3">3. How We Use Your Information</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>To authenticate you and manage your account</li>
            <li>To display your Google Drive images for product image selection</li>
            <li>To analyze product images using AI and generate descriptions</li>
            <li>To synchronize your product catalog with Square</li>
            <li>To provide and improve the Service</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Data Sharing</h2>
          <p>
            We do not sell your personal information. We share data only with:
          </p>
          <ul className="list-disc pl-6 space-y-2 mt-2">
            <li><strong>Google</strong> — for authentication and Drive access (via OAuth)</li>
            <li><strong>Square</strong> — to sync your product catalog (via your Square account)</li>
            <li><strong>AI providers</strong> — product images are sent to AI services for analysis;
              images are processed and not retained by the AI provider</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Data Security</h2>
          <p>
            We use industry-standard security measures including HTTPS encryption, secure token
            storage, and access controls. OAuth refresh tokens are stored encrypted in our database.
            We do not store your Google or Square passwords.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Data Retention and Deletion</h2>
          <p>
            Your account data is retained as long as your account is active. You can disconnect
            Google Drive at any time from your account settings, which immediately deletes your
            stored Drive refresh token. To request full account deletion, contact us at the email
            below.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Your Rights</h2>
          <p>You have the right to:</p>
          <ul className="list-disc pl-6 space-y-2 mt-2">
            <li>Access the personal data we hold about you</li>
            <li>Request correction of inaccurate data</li>
            <li>Request deletion of your account and data</li>
            <li>Disconnect third-party services (Google Drive, Square) at any time</li>
            <li>Revoke Google access via your{' '}
              <a
                href="https://myaccount.google.com/permissions"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                Google Account permissions
              </a>
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any
            material changes by posting the new policy on this page with an updated date.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Contact Us</h2>
          <p>
            If you have questions about this Privacy Policy or your data, contact us at:{' '}
            <a
              href="mailto:privacy@windingriversoftware.com"
              className="text-blue-600 hover:underline"
            >
              privacy@windingriversoftware.com
            </a>
          </p>
        </section>
      </div>
    </div>
  );
}
